import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { PasswordInput } from '@/components/ui/password-input'

import {
  PASSWORD_REGEX,
  PasswordRequirements
} from './PasswordRequirements'

export interface PasswordFormValues {
  password: string
  password_confirmation: string
  old_password?: string
}

interface PasswordFormProps {
  onSubmit: (values: PasswordFormValues) => void
  oldPassword?: boolean
  isPending?: boolean
}

export function PasswordForm({
  onSubmit,
  oldPassword = false,
  isPending
}: PasswordFormProps) {
  const { t } = useTranslation('settings')

  const schema = useMemo(
    () =>
      z
        .object({
          old_password: oldPassword
            ? z.string().min(1, t('form.old_password_required'))
            : z.string().optional(),
          password: z.string().regex(PASSWORD_REGEX),
          password_confirmation: z
            .string()
            .min(1, t('form.confirm_password_required'))
        })
        .refine((d) => d.password === d.password_confirmation, {
          path: ['password_confirmation'],
          message: t('form.passwords_do_not_match')
        }),
    [t, oldPassword]
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      old_password: '',
      password: '',
      password_confirmation: ''
    }
  })

  const password = form.watch('password') || ''

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((v) => onSubmit(v as PasswordFormValues))}
        className="space-y-5"
      >
        {oldPassword && (
          <FormField
            control={form.control}
            name="old_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.old_password_label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    placeholder={t('form.old_password_placeholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.password_label')}</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder={t('form.password_placeholder')}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <PasswordRequirements password={password} />

        <FormField
          control={form.control}
          name="password_confirmation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.confirm_password_label')}</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder={t('form.confirm_password_placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={isPending}
        >
          {t('form.submit_button')}
        </Button>
      </form>
    </Form>
  )
}
